import type { EventAiTagVariant, EventAlarmDetection } from '../../../core/models/entities/event-detail.model';
import type { DetectionBoundingBox } from '../../../core/models/entities/event-playback.model';
import { detectionVariant } from '../../../core/api/dto-mappers';
import { alarmClipFrameIndexesForGroup } from './alarm-image-clip.util';
import { parseEventAlarmTimestamp } from './playback-window.util';

const EVENT_TYPE_ORDINAL: Record<EventAiTagVariant, number> = {
  motion: 0,
  human: 1,
  vehicle: 2,
  gun: 3,
};

const EVENT_TYPES: EventAiTagVariant[] = ['motion', 'human', 'vehicle', 'gun'];
const FALLBACK_TIMELINE_SECONDS = 120;

/** Whether this second should have AI activity (matches mock generator). */
function isActiveSecond(numericId: number, second: number): boolean {
  const seed = numericId * 131 + second * 17;
  const wave = Math.sin((second + (numericId % 7)) / 8) * 0.35 + 0.55;
  const noise = seededUnit(seed) * 0.25;
  const activity = wave + noise;

  if (second < 5) {
    return seededUnit(seed + 1) > 0.15;
  }
  if (second >= FALLBACK_TIMELINE_SECONDS - 8) {
    return seededUnit(seed + 2) > 0.4;
  }
  return activity > 0.42;
}

/** 1–4 distinct tag types for this second (capped by enabled types). */
function tagsForSecond(
  numericId: number,
  second: number,
  enabled: EventAiTagVariant[],
): EventAiTagVariant[] {
  if (!isActiveSecond(numericId, second)) {
    return [];
  }

  const seed = numericId * 89 + second * 23;
  const maxThisSecond = Math.min(4, enabled.length);
  const count = 1 + Math.floor(seededUnit(seed) * maxThisSecond);

  const ranked = [...enabled].sort((a, b) => {
    const bias =
      seededUnit(seed + EVENT_TYPE_ORDINAL[a] * 3) - seededUnit(seed + EVENT_TYPE_ORDINAL[b] * 3);
    return bias !== 0 ? bias : EVENT_TYPE_ORDINAL[b] - EVENT_TYPE_ORDINAL[a];
  });

  const picked: EventAiTagVariant[] = [];
  for (const type of ranked) {
    if (picked.length >= count) {
      break;
    }
    if (!picked.includes(type)) {
      picked.push(type);
    }
  }

  return picked;
}

interface MockTrack {
  trackId: string;
  eventType: EventAiTagVariant;
  x: number;
  y: number;
  width: number;
  height: number;
  vx: number;
  vy: number;
}

function createMockTracks(numericId: number, enabled: EventAiTagVariant[]): MockTrack[] {
  const tracks: MockTrack[] = [];

  for (const eventType of enabled) {
    const trackCount = eventType === 'motion' ? 2 : 1;
    for (let index = 0; index < trackCount; index++) {
      const seed = numericId * 1000 + EVENT_TYPE_ORDINAL[eventType] * 100 + index * 17;
      const width = roundPercent(4 + seededUnit(seed + 2) * 7);
      const height = roundPercent(8 + seededUnit(seed + 3) * 8);

      tracks.push({
        trackId: `track-${numericId}-${eventType}-${index}`,
        eventType,
        x: roundPercent(10 + seededUnit(seed) * 55),
        y: roundPercent(10 + seededUnit(seed + 1) * 45),
        width,
        height,
        vx: roundPercent((seededUnit(seed + 4) - 0.5) * 1.4),
        vy: roundPercent((seededUnit(seed + 5) - 0.5) * 1.0),
      });
    }
  }

  return tracks;
}

function stepMockTrack(track: MockTrack, second: number, numericId: number): void {
  const seed = numericId * 77 + second * 13 + track.trackId.length * 3;
  const jitterX = (seededUnit(seed) - 0.5) * 0.35;
  const jitterY = (seededUnit(seed + 1) - 0.5) * 0.25;

  let nextX = track.x + track.vx + jitterX;
  let nextY = track.y + track.vy + jitterY;

  if (nextX < 2 || nextX + track.width > 92) {
    track.vx = -track.vx;
    nextX = clampPercent(nextX);
  }
  if (nextY < 2 || nextY + track.height > 88) {
    track.vy = -track.vy;
    nextY = clampPercent(nextY);
  }

  track.x = clampPercent(roundPercent(Math.max(2, Math.min(92 - track.width, nextX))));
  track.y = clampPercent(roundPercent(Math.max(2, Math.min(88 - track.height, nextY))));
}

function pickMockTrackForType(
  tracks: MockTrack[],
  eventType: EventAiTagVariant,
  second: number,
  numericId: number,
): MockTrack | null {
  const candidates = tracks.filter((track) => track.eventType === eventType);
  if (!candidates.length) {
    return null;
  }
  const index = Math.floor(
    seededUnit(numericId * 53 + second * 19 + EVENT_TYPE_ORDINAL[eventType] * 7) * candidates.length,
  );
  return candidates[index] ?? null;
}

/** Deterministic pseudo-random in [0, 1). */
function seededUnit(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function roundPercent(value: number): number {
  return Math.round(value * 10) / 10;
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}

/** Overlay percents stay stable in CSS (`55%`, not `55.00000000000001%`). */
function overlayPercent(part: number, whole: number): number {
  return clampPercent(Math.round((part / whole) * 100 * 10000) / 10000);
}

/** Same numericId + eventType → same box every time (mock consistency). */
export function seededDetectionBox(
  numericId: number,
  eventType: EventAiTagVariant,
  offsetSeconds = 0,
  index = 0,
): DetectionBoundingBox {
  const seedBase = numericId * 31 + EVENT_TYPE_ORDINAL[eventType] * 17 + index * 13;
  const width = 4 + seededUnit(seedBase + 1) * 6;
  const height = 8 + seededUnit(seedBase + 2) * 6;
  const x = 5 + seededUnit(seedBase + 3) * (90 - width);
  const y = 5 + seededUnit(seedBase + 4) * (90 - height);

  return {
    id: `det-${numericId}-${eventType}-${index}`,
    eventType,
    x: roundPercent(x),
    y: roundPercent(y),
    width: roundPercent(width),
    height: roundPercent(height),
    offsetSeconds,
  };
}

export function generateTimedDetectionSequence(
  numericId: number,
  durationSeconds = FALLBACK_TIMELINE_SECONDS,
  baseTypes: EventAiTagVariant[] = EVENT_TYPES,
): DetectionBoundingBox[] {
  const enabled = baseTypes.length > 0 ? baseTypes : EVENT_TYPES;
  const tracks = createMockTracks(numericId, enabled);
  const boxes: DetectionBoundingBox[] = [];
  let seq = 0;

  for (let second = 0; second < durationSeconds; second++) {
    for (const track of tracks) {
      stepMockTrack(track, second, numericId);
    }

    const types = tagsForSecond(numericId, second, enabled);
    for (const eventType of types) {
      const track = pickMockTrackForType(tracks, eventType, second, numericId);
      if (!track) {
        continue;
      }

      seq += 1;
      boxes.push({
        id: `det-${numericId}-s${second}-${eventType}-${seq}`,
        trackId: track.trackId,
        eventType,
        offsetSeconds: second,
        x: track.x,
        y: track.y,
        width: track.width,
        height: track.height,
      });
    }
  }

  return boxes;
}

export function boxesForEnabledEvents(
  numericId: number,
  enabled: EventAiTagVariant[],
): DetectionBoundingBox[] {
  return generateTimedDetectionSequence(numericId, FALLBACK_TIMELINE_SECONDS, enabled);
}

/**
 * Build one timed bounding-box track for every AI timeline segment.
 * `offsetSeconds` is relative to `origin` (library timeline start) so boxes line up
 * with Caliber playhead time while the playhead is inside that event.
 */
export function buildDetectionBoxesFromAiSegments(
  numericId: number,
  segments: Record<string, Array<{ start: Date | string; end: Date | string }>>,
  origin: Date,
): DetectionBoundingBox[] {
  const originMs = origin.getTime();
  if (!Number.isFinite(originMs)) {
    return [];
  }

  const boxes: DetectionBoundingBox[] = [];
  let seq = 0;

  for (const eventType of EVENT_TYPES) {
    const list = segments[eventType] ?? [];
    for (let segIndex = 0; segIndex < list.length; segIndex++) {
      const start = new Date(list[segIndex]!.start);
      const end = new Date(list[segIndex]!.end);
      const startMs = start.getTime();
      const endMs = end.getTime();
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
        continue;
      }

      const startOffsetSec = Math.floor((startMs - originMs) / 1000);
      const endOffsetSec = Math.max(startOffsetSec + 1, Math.ceil((endMs - originMs) / 1000));
      const tracks = createMockTracks(numericId + segIndex * 97, [eventType]);
      const track = tracks[0];
      if (!track) {
        continue;
      }
      track.trackId = `track-${numericId}-${eventType}-seg${segIndex}`;

      for (let second = startOffsetSec; second < endOffsetSec; second++) {
        if (second < 0) {
          continue;
        }
        stepMockTrack(track, second, numericId);
        seq += 1;
        boxes.push({
          id: `det-${numericId}-${eventType}-s${second}-${seq}`,
          trackId: track.trackId,
          eventType,
          offsetSeconds: second,
          timestamp: new Date(originMs + second * 1000).toISOString(),
          x: track.x,
          y: track.y,
          width: track.width,
          height: track.height,
        });
      }
    }
  }

  return boxes;
}

export function normalizeDetectionBoxes(boxes: DetectionBoundingBox[]): DetectionBoundingBox[] {
  return boxes.map((box, index) => ({
    ...box,
    id: box.id ?? `det-legacy-${index}`,
    offsetSeconds: box.offsetSeconds ?? 0,
  }));
}

/** Keep the portion of a pixel rect that still lies inside the source frame. */
function clipPixelRectToFrame(
  left: number,
  top: number,
  width: number,
  height: number,
  frameWidth: number,
  frameHeight: number,
): { left: number; top: number; width: number; height: number } | null {
  const clippedLeft = Math.max(0, left);
  const clippedTop = Math.max(0, top);
  const clippedRight = Math.min(frameWidth, left + width);
  const clippedBottom = Math.min(frameHeight, top + height);
  const clippedWidth = clippedRight - clippedLeft;
  const clippedHeight = clippedBottom - clippedTop;
  if (clippedWidth <= 0 || clippedHeight <= 0) {
    return null;
  }
  return {
    left: clippedLeft,
    top: clippedTop,
    width: clippedWidth,
    height: clippedHeight,
  };
}

/** Maps alarm `DetectedObjects` boxes into overlay percent boxes for clip playback. */
function detectionBoundsToPercent(
  bounds: EventAlarmDetection['bounds'],
  sourceWidth: number,
  sourceHeight: number,
): Pick<DetectionBoundingBox, 'x' | 'y' | 'width' | 'height'> {
  // Some VMS exports use normalized 0–1 coords (top-left). Pixel exports measure Top
  // upward from the bottom of the frame to the top edge of the box; Height extends down.
  const normalized =
    bounds.left <= 1 &&
    bounds.top <= 1 &&
    bounds.width <= 1 &&
    bounds.height <= 1;
  if (normalized) {
    return {
      x: clampPercent(bounds.left * 100),
      y: clampPercent(bounds.top * 100),
      width: clampPercent(bounds.width * 100),
      height: clampPercent(bounds.height * 100),
    };
  }

  const clipped = clipPixelRectToFrame(
    bounds.left,
    sourceHeight - bounds.top,
    bounds.width,
    bounds.height,
    sourceWidth,
    sourceHeight,
  );
  if (!clipped) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  return {
    x: overlayPercent(clipped.left, sourceWidth),
    y: overlayPercent(clipped.top, sourceHeight),
    width: overlayPercent(clipped.width, sourceWidth),
    height: overlayPercent(clipped.height, sourceHeight),
  };
}

export function detectionBoxesFromAlarmDetections(
  detections: readonly EventAlarmDetection[] | null | undefined,
  alarmId?: string | null,
  timestamp?: string | null,
): DetectionBoundingBox[] {
  if (!detections?.length) {
    return [];
  }

  const boxes: DetectionBoundingBox[] = [];
  detections.forEach((detection, index) => {
    const sourceWidth = detection.sourceWidth > 0 ? detection.sourceWidth : 1920;
    const sourceHeight = detection.sourceHeight > 0 ? detection.sourceHeight : 1080;
    const bounds = detectionBoundsToPercent(detection.bounds, sourceWidth, sourceHeight);
    if (bounds.width <= 0 || bounds.height <= 0) {
      return;
    }
    boxes.push({
      id: alarmId ? `${alarmId}-${index}` : `alarm-det-${index}`,
      eventType: detectionVariant(detection.name, 'motion'),
      ...bounds,
      offsetSeconds: 0,
      timestamp: timestamp ?? undefined,
    });
  });
  return boxes;
}

/**
 * Alarm-clip overlays: only nested `frameGroupIndex` detections draw, stamped onto their
 * frame band with `offsetSeconds` = 0-based frame index (overlay filters by frame index).
 * `trackId` is stable per event-type ordinal across groups so the overlay can CSS-transition
 * when the clip advances from one band to the next.
 */
export function stampAlarmClipDetectionBoxes(
  detections: readonly EventAlarmDetection[] | null | undefined,
  frameCount: number,
  alarmId?: string | null,
  timestamp?: string | null,
): DetectionBoundingBox[] {
  if (!detections?.length || frameCount <= 0) {
    return [];
  }

  const ordinalByGroupAndType = new Map<string, number>();
  const stamped: DetectionBoundingBox[] = [];
  const idPrefix = alarmId ?? 'alarm-det';

  detections.forEach((detection, index) => {
    if (detection.frameGroupIndex == null) {
      return;
    }
    const frames = alarmClipFrameIndexesForGroup(detection.frameGroupIndex, frameCount);
    if (!frames.length) {
      return;
    }
    const bases = detectionBoxesFromAlarmDetections([detection], null, timestamp);
    for (const base of bases) {
      const ordinalKey = `${detection.frameGroupIndex}:${base.eventType}`;
      const ordinal = ordinalByGroupAndType.get(ordinalKey) ?? 0;
      ordinalByGroupAndType.set(ordinalKey, ordinal + 1);
      // Same event-type ordinal across groups shares a track so group→group moves can tween.
      const trackId = `${idPrefix}:${base.eventType}:${ordinal}`;
      for (const frameIndex of frames) {
        stamped.push({
          ...base,
          id: `${idPrefix}-${index}-f${frameIndex}`,
          trackId,
          offsetSeconds: frameIndex,
        });
      }
    }
  });
  return stamped;
}

export function filterBoxesByEventTypes(
  boxes: DetectionBoundingBox[],
  visibleTypes: EventAiTagVariant[],
): DetectionBoundingBox[] {
  const normalized = normalizeDetectionBoxes(boxes);
  if (!visibleTypes.length) {
    return normalized;
  }
  const allowed = new Set(visibleTypes);
  return normalized.filter((box) => allowed.has(box.eventType));
}

/** Show boxes for the current whole playback second only (1 position update per second). */
export function filterBoxesAtPlaybackSecond(
  boxes: DetectionBoundingBox[],
  activeOffsetSeconds: number,
): DetectionBoundingBox[] {
  const second = Math.floor(activeOffsetSeconds);
  return normalizeDetectionBoxes(boxes).filter(
    (box) => Math.floor(box.offsetSeconds ?? 0) === second,
  );
}

export function filterBoxesByActiveTime(
  boxes: DetectionBoundingBox[],
  activeOffsetSeconds: number,
): DetectionBoundingBox[] {
  return filterBoxesAtPlaybackSecond(boxes, activeOffsetSeconds);
}

/** Recompute offsetSeconds from alarm timestamps relative to the active playback window. */
export function syncDetectionOffsetsForWindow(
  boxes: DetectionBoundingBox[],
  windowStart: Date,
  windowDurationSeconds: number,
): DetectionBoundingBox[] {
  const windowStartMs = windowStart.getTime();
  const windowEndMs = windowStartMs + windowDurationSeconds * 1000;

  return normalizeDetectionBoxes(boxes)
    .map((box) => {
      const alarmTime = parseEventAlarmTimestamp(box.timestamp);
      if (!alarmTime) {
        return box;
      }

      const alarmMs = alarmTime.getTime();
      if (alarmMs < windowStartMs || alarmMs > windowEndMs) {
        return null;
      }

      return {
        ...box,
        offsetSeconds: (alarmMs - windowStartMs) / 1000,
      };
    })
    .filter((box): box is DetectionBoundingBox => box !== null);
}

/** Keep the selected alarm visible even when the playback anchor is momentarily unavailable. */
export function pickDetectionForAlarm(
  boxes: DetectionBoundingBox[],
  alarmId: string,
  windowStart: Date,
  windowDurationSeconds: number,
): DetectionBoundingBox[] {
  const match = normalizeDetectionBoxes(boxes).find((box) => box.id === alarmId);
  if (!match) {
    return [];
  }

  const alarmTime = parseEventAlarmTimestamp(match.timestamp);
  if (!alarmTime) {
    return [{ ...match, offsetSeconds: 0 }];
  }

  const windowStartMs = windowStart.getTime();
  const windowEndMs = windowStartMs + windowDurationSeconds * 1000;
  const alarmMs = alarmTime.getTime();
  const offsetSeconds =
    alarmMs >= windowStartMs && alarmMs <= windowEndMs
      ? (alarmMs - windowStartMs) / 1000
      : 0;

  return [{ ...match, offsetSeconds }];
}
